import React, { useState, useEffect, memo } from 'react';
import { getDisplayableImageUrl, isHeicFile } from 'utils/heicUtils';
import { getSignedUrl } from 'modules/FileManagerV2/api/services';

import fileImage from 'assets/images/file.png';
import docImage from 'assets/images/doc.png';
import videoImage from 'assets/images/video.png';
import textImage from 'assets/images/text.png';
import xlsImage from 'assets/images/xls.png';
import pdfImage from 'assets/images/pdf.png';

function getStaticThumb(type) {
  switch (type) {
    case 'video':
      return videoImage;
    case 'pdf':
      return pdfImage;
    case 'doc':
    case 'ppt':
      return docImage;
    case 'xls':
    case 'csv':
      return xlsImage;
    case 'text':
      return textImage;
    default:
      return fileImage;
  }
}

/**
 * Thumbnail item for the virtualized strip.
 * Fixes V1 ThumbnailComponent issues:
 *  - HEIC blob URLs are revoked on cleanup (memory leak fix)
 *  - No array-index keys (handled by ThumbnailStrip via item id)
 */
const ThumbnailItem = ({ type, url, file_path, display_name, isSelected }) => {
  // Seed with null; the useEffect below sets the real URL after mount.
  // Using null avoids a stale initial value when the component re-mounts
  // with a different url (e.g. after a presigned URL refresh).
  const [displayUrl, setDisplayUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (type !== 'image') {
      setDisplayUrl(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        // Resolve the image URL: use the pre-signed URL if already available,
        // otherwise fetch it from S3 via Amplify (tenant-specific bucket).
        let resolvedUrl = url;
        if (!resolvedUrl && file_path) {
          resolvedUrl = await getSignedUrl({ file_path });
        }

        if (!resolvedUrl || cancelled) return;

        if (display_name && isHeicFile(display_name)) {
          // Lower quality for thumbnails (100px display) — full viewer uses default 0.8
          const converted = await getDisplayableImageUrl(resolvedUrl, display_name, 0.3);
          if (!cancelled) setDisplayUrl(converted);
        } else {
          if (!cancelled) setDisplayUrl(resolvedUrl);
        }
      } catch {
        if (!cancelled && url) setDisplayUrl(url);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    // Do NOT revoke blob URLs here — the module-level heicCache owns their
    // lifecycle and reuses them when this item remounts (e.g. virtual scroll).
    // Blob URLs are revoked en-masse by clearHeicCache() when the modal closes.
    return () => {
      cancelled = true;
    };
  }, [type, url, file_path, display_name]);

  const src = type === 'image' ? displayUrl || url : getStaticThumb(type);

  return (
    <div className={`thumbnail-item${isSelected ? ' thumbnail-item--selected' : ''}`}>
      <div className="thumbnail-item__img-wrap">
        {isLoading ? (
          <div className="thumbnail-item__spinner">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        ) : (
          <img src={src} alt={display_name} className="thumbnail-item__img" />
        )}
      </div>
      <span className="thumbnail-item__name" title={display_name}>
        {display_name}
      </span>
    </div>
  );
};

export default memo(ThumbnailItem);
